import { LocalizedLink as Link } from "@/components/LocalizedLink";
import { ArrowRight, Star, Users, BedDouble, Bath, Maximize } from "lucide-react";
import { useLang } from "@/i18n";
import { UNITS } from "@/data/site";
import { Reveal, MaskedLines } from "@/components/motion";
import { usePageMeta } from "@/hooks/usePageMeta";
import UnitStatus from "@/components/UnitStatus";
import FromPrice from "@/components/FromPrice";

const UnitsPage = () => {
  const { t, lang } = useLang();
  usePageMeta({ title: t.seo.unitsPage.title, description: t.seo.unitsPage.description });
  const u = t.unitsPage;

  return (
    <div data-testid="units-page">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-40">
        <Reveal y={20}>
          <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-terracotta">{u.overline}</p>
        </Reveal>
        <h1 className="font-display font-medium text-5xl md:text-7xl tracking-tight mt-4" data-testid="units-title">
          <MaskedLines lines={[u.title]} delay={0.05} />
        </h1>
        <Reveal delay={0.15}>
          <p className="mt-7 max-w-2xl text-stone text-base md:text-lg leading-relaxed font-light" data-testid="units-sub">
            {u.sub}
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-12 mt-12 md:mt-24 space-y-6 md:space-y-36">
        {UNITS.map((unit, i) => (
          <div key={unit.slug} className="grid md:grid-cols-12 gap-6 md:gap-10 items-center max-md:rounded-2xl max-md:border max-md:border-linen max-md:bg-card max-md:p-3 max-md:pb-6" data-testid={`units-block-${unit.slug}`}>
            <Reveal className={`md:col-span-7 ${i % 2 === 1 ? "md:order-2" : ""}`}>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <div className="col-span-2 overflow-hidden rounded-2xl aspect-[16/10] group">
                  {unit.cover ? (
                    <img
                      src={unit.cover}
                      alt={unit.name}
                      loading={i === 0 ? "eager" : "lazy"}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : <div className="w-full h-full bg-sand" />}
                </div>
                {unit.images.slice(1, 3).map((src) => (
                  <div key={src} className="hidden md:block overflow-hidden rounded-xl aspect-[4/3] group">
                    <img
                      src={src}
                      alt={unit.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.12} className={`md:col-span-5 max-md:px-2 ${i % 2 === 1 ? "md:order-1" : ""}`}>
              <p className="text-xs uppercase tracking-[0.2em] text-stone">{unit.tagline[lang]}</p>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-2 md:mt-3">{unit.name}</h2>
              <UnitStatus unit={unit} className="mt-3" />
              <FromPrice unit={unit} className="mt-2" />
              {unit.reviewsCount > 0 && (
                <div className="flex items-center gap-1.5 mt-4" data-testid={`units-rating-${unit.slug}`}>
                  <Star className="h-4 w-4 fill-terracotta text-terracotta" />
                  <span className="text-sm font-semibold">{unit.rating}</span>
                  <span className="text-sm text-stone">· {unit.reviewsCount} {t.unitPage.reviews}</span>
                </div>
              )}
              <p className="md:hidden mt-4 text-sm text-stone">
                {[
                  `${unit.specs.guests} ${u.guests}`,
                  unit.specs.bedrooms === 1 ? u.studio : `${unit.specs.bedrooms} ${u.bedrooms}`,
                  `${unit.specs.beds} ${u.beds}`,
                  `${unit.specs.bathrooms} ${u.bathrooms}`,
                  unit.specs.sqm ? `${unit.specs.sqm} ${u.sqm}` : null,
                ].filter(Boolean).join(" · ")}
              </p>
              <div className="hidden md:flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 text-sm text-stone">
                <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4 text-terracotta" />{unit.specs.guests} {u.guests}</span>
                <span className="inline-flex items-center gap-1.5"><BedDouble className="h-4 w-4 text-terracotta" />{unit.specs.bedrooms} {unit.specs.bedrooms === 1 ? u.studio : u.bedrooms} · {unit.specs.beds} {u.beds}</span>
                <span className="inline-flex items-center gap-1.5"><Bath className="h-4 w-4 text-terracotta" />{unit.specs.bathrooms} {u.bathrooms}</span>
                {unit.specs.sqm && (
                  <span className="inline-flex items-center gap-1.5"><Maximize className="h-4 w-4 text-terracotta" />{unit.specs.sqm} {u.sqm}</span>
                )}
              </div>
              <p className="mt-4 md:mt-5 text-stone font-light leading-relaxed max-w-md">{unit.teaser[lang]}</p>
              <div className="mt-6 md:mt-8 grid grid-cols-2 md:flex md:flex-wrap items-center gap-3 md:gap-4">
                <Link
                  to={unit.path}
                  data-testid={`units-discover-${unit.slug}`}
                  className={`group inline-flex items-center justify-center gap-2 md:gap-3 rounded-full bg-terracotta px-4 md:px-7 py-3.5 text-sm font-medium text-cream hover:bg-terracotta-dark transition-colors duration-300 ${unit.bookable ? "" : "col-span-2 md:col-span-1"}`}
                >
                  {u.discover}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                </Link>
                {unit.bookable && (
                  <Link
                    to={`/prenota/${unit.slug}`}
                    data-testid={`units-book-${unit.slug}`}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-ink/20 px-4 md:px-7 py-3.5 text-sm font-medium hover:border-terracotta hover:text-terracotta transition-colors duration-300"
                  >
                    {t.nav.book}
                  </Link>
                )}
              </div>
            </Reveal>
          </div>
        ))}
      </section>

      <section className="px-3 md:px-6 mt-24 md:mt-32" data-testid="units-cta">
        <Reveal>
          <div className="rounded-[2rem] bg-terracotta text-cream px-8 md:px-16 py-16 md:py-24 flex flex-col md:flex-row md:items-end justify-between gap-10">
            <div className="max-w-2xl">
              <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-tight">{t.homeCta.title}</h2>
              <p className="mt-5 text-cream text-base md:text-lg font-light">{t.homeCta.sub}</p>
            </div>
            <Link
              to="/prenota"
              data-testid="units-cta-book-button"
              className="group shrink-0 self-start md:self-auto inline-flex items-center gap-3 rounded-full bg-cream text-ink px-8 py-4 text-sm md:text-base font-medium hover:bg-sand transition-colors duration-300"
            >
              {t.homeCta.cta}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default UnitsPage;
